import "./bootstrap";
import "../css/app.css";
import 'primeicons/primeicons.css';
import "./errorHandlers";
import { trackApiPerformance, initPerformanceTracker } from "@/services/performanceTracker";
import DICreateInertiaApp from "@/micro-apps/create-app";
import "./pageVisibility.js";

import { useAppStore } from "@/store/index";
import { captureLocation, getCookie } from "./helpers";
import { logError } from "@/services/logger";

// ─────────────────────────────────────────────
// Cross-browser UUID helper
// ─────────────────────────────────────────────
function generateRequestId(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    // Fallback: RFC 4122 UUID v4 using getRandomValues or Math.random
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r =
            typeof crypto !== "undefined" && crypto.getRandomValues
                ? crypto.getRandomValues(new Uint8Array(1))[0] % 16
                : (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

// ─────────────────────────────────────────────
// CSRF Token Setup
// ─────────────────────────────────────────────
const axios = window.axios;

const getXsrfToken = (): string =>
    document.cookie.replace(
        /(?:(?:^|.*;\s*)XSRF-TOKEN\s*=\s*([^;]*).*$)|^.*$/,
        "$1"
    );

const getCsrfMeta = (): string | null =>
    (document.head.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? null;

const csrfToken = getCsrfMeta();
if (csrfToken) {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
}

// Wire performanceTracker to use logError() so recursion guard is respected
initPerformanceTracker(logError);

// ─────────────────────────────────────────────
// Axios Request Interceptor
// ─────────────────────────────────────────────
axios.interceptors.request.use(
    async (config) => {
        (config as any).metadata = { startTime: Date.now() };

        // Attach unique request ID for Nightwatch trace correlation
        const requestId = generateRequestId();
        (config as any).requestId = requestId;
        config.headers["X-Request-ID"] = requestId;

        const appStore = useAppStore();
        const token = appStore.token || getCookie("token");
        const xsrfToken = getXsrfToken();

        config = await captureLocation(config);

        if (xsrfToken) {
            config.headers["X-XSRF-TOKEN"] = decodeURIComponent(xsrfToken);
        }
        if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
            config.headers["X-Requested-With"] = "XMLHttpRequest";
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// ─────────────────────────────────────────────
// Axios Response Interceptor
// ─────────────────────────────────────────────
axios.interceptors.response.use(
    (response) => {
        // Track performance — skip the error endpoint itself to prevent a
        // 419 → logError → trackApiPerformance → logError feedback loop
        const start = (response.config as any).metadata?.startTime as number | undefined;
        if (!response.config.url?.includes("/frontend-error")) {
            trackApiPerformance(
                response.config.url,
                response.config.method,
                start ? Date.now() - start : 0,
                response.status
            );
        }

        // Sync user/token from headers
        const appStore = useAppStore();
        syncStoreFromHeaders(appStore, response.headers);

        return response;
    },

    async (error) => {
        const start = (error.config as any)?.metadata?.startTime as number | undefined;
        const duration = start ? Date.now() - start : 0;
        const status = error.response?.status;

        // Track performance even on errors
        trackApiPerformance(error.config?.url, error.config?.method, duration, status);

        // Network error (no response at all) — Laravel never sees this, so log it.
        if (!error.response) {
            logError({
                type: "network_error",
                message: error.message,
                url: error.config?.url,
                context: {
                    request_id: error.config?.requestId,
                    page: window.location.pathname,
                    query: window.location.search,
                },
            });
            return Promise.reject(error);
        }


        const appStore = useAppStore();
        syncStoreFromHeaders(appStore, error.response.headers);

        // Handle CSRF expiry (419) — auto refresh token
        if (status === 419 && !error.config._retry) {
            return await handleCsrfRefresh(error);
        }

        // Build user-facing notification
        const errorMsg = resolveErrorMessage(error);
        if (errorMsg) {
            appStore.setNotification({
                heading: "Failed!",
                subHeading: errorMsg,
                type: "error",
            });
        }

        return Promise.reject(error);
    }
);

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

/** Sync user/token from response headers into Pinia store */
function syncStoreFromHeaders(store: ReturnType<typeof useAppStore>, headers: Record<string, any>): void {
    if ("user" in headers) store.user = JSON.parse(headers["user"]);
    if ("token" in headers) store.token = headers["token"];
}

/** Extract a readable error message from an Axios error */
function resolveErrorMessage(error: any): string | null {
    const status = error.response?.status;
    if (status === 422) {
        const errors = error.response.data.errors || error.response.data.message;
        if (typeof errors === "string") return errors;
        if (typeof errors === "object" && Object.keys(errors).length > 0) {
            return errors[Object.keys(errors)[0]][0];
        }
    }
    return error.response?.data?.message ?? null;
}

/** Auto-retry request after refreshing CSRF token on 419 */
async function handleCsrfRefresh(error: any): Promise<any> {
    error.config._retry = true;
    try {
        const response = await axios.get(route("csrf.token"));
        const newToken = response.data.csrf_token;

        document.querySelector('meta[name="csrf-token"]')?.setAttribute("content", newToken);
        axios.defaults.headers.common["X-CSRF-TOKEN"] = newToken;
        error.config.headers["X-CSRF-TOKEN"] = newToken;

        return axios(error.config);
    } catch (tokenError) {
        window.location.reload();
    }
}

// ─────────────────────────────────────────────
// Global Error Boundaries
// ─────────────────────────────────────────────

/** Catch unhandled Promise rejections */
window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    logError({
        type: "unhandled_rejection",
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack?.substring(0, 2000),
        context: {
            page: window.location.pathname,
            query: window.location.search,
        },
    });
});

/** Catch uncaught JS errors (e.g. script errors outside Vue) */
window.addEventListener("error", (event: ErrorEvent) => {
    // Skip resource load errors (img/script/link) — handled by errorHandlers.ts
    if (!event.error) return;
    logError({
        type: "global_error",
        message: event.message,
        stack: event.error?.stack?.substring(0, 2000),
        url: event.filename,
        context: {
            line: event.lineno,
            col: event.colno,
            page: window.location.pathname,
            query: window.location.search,
        },
    });
});

// ─────────────────────────────────────────────
// Vue App Bootstrap
// ─────────────────────────────────────────────
const createInstance = () => {
    const app: any = DICreateInertiaApp();

    if (app?.config) {
        /** Vue component-level error handler */
        app.config.errorHandler = (err: any, instance: any, info: any) => {
            logError({
                type: "vue_error",
                message: err.message,
                stack: err.stack?.substring(0, 2000),
                component: instance?.type?.name || instance?.type?.__file,
                info,
                context: {
                    page: window.location.pathname,
                    query: window.location.search,
                },
            });
        };

        /** Vue warnings in dev (optional: remove in prod) */
        if (import.meta.env.DEV) {
            app.config.warnHandler = (msg: string, instance: any, trace: string) => {
                console.warn(`[Vue warn]: ${msg}\n${trace}`);
            };
        }
    }
};

(async () => {
    try {
        createInstance();
    } catch (e) {
        logError({
            type: "global_error",
            message: (e as any)?.message || "App bootstrap failed",
            stack: (e as any)?.stack?.substring(0, 2000),
        });
    }
})();

// Detect slow page loads — useful for ERP dashboard regressions
window.addEventListener("load", () => {
    const perf = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming;
    const loadTime = perf.loadEventEnd - perf.startTime;

    if (loadTime > 3000) {
        logError({
            type: "global_error",
            message: `Slow page load detected (${Math.round(loadTime)}ms)`,
            context: {
                load_time: loadTime,
                page: window.location.pathname,
                query: window.location.search,
            },
        });
    }
});